import type { ColumnDef } from "@tanstack/react-table";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import type { Person } from "@/hooks/api/use-persons";
import {
  DEFAULT_COLUMN_WIDTH,
  DEFAULT_PRIMARY_COLUMN_WIDTHS,
} from "@/lib/column-defaults";
import { formatDate } from "@/lib/date-formatting";
import { createCustomFieldColumns } from "@/pages/accounts/account-list/custom-field-column-utils";
import type { CustomFieldDefinition } from "@/types/custom-fields";
import type { UnifiedTableColumn } from "@/types/table-column";

export const getPersonColumns = (
  _filterOptions?: Record<string, never>,
  customFields?: CustomFieldDefinition[],
): UnifiedTableColumn<Person>[] => {
  // Start with standard columns
  const standardColumns: UnifiedTableColumn<Person>[] = [
    {
      key: "name",
      title: "Name",
      sortable: true,
      filterable: true,
      filterType: "text",
      defaultSortDirection: "asc",
      nullSort: "last",
      required: true, // This is the primary column that cannot be hidden
      size: DEFAULT_PRIMARY_COLUMN_WIDTHS.MEDIUM,
      render: (person: Person) => {
        const name = [person.first_name, person.last_name]
          .filter(Boolean)
          .join(" ");
        return name || person.email_address || "-";
      },
      exportValue: (person: Person) => {
        const name = [person.first_name, person.last_name]
          .filter(Boolean)
          .join(" ");
        return name || person.email_address || "";
      },
    },
    {
      key: "email_address",
      title: "Email",
      sortable: true,
      filterable: true,
      filterType: "text",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 250, // Fixed
      render: (person: Person) => person.email_address || "-",
    },
    {
      key: "job_title",
      title: "Job Title",
      sortable: true,
      filterable: true,
      filterType: "text",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 180, // Fixed
      render: (person: Person) => person.job_title || "-",
    },
    {
      key: "is_employee",
      title: "Is Employee",
      sortable: true,
      filterable: true,
      filterType: "select",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 120, // Fixed
      filterOptions: [
        { id: "true", label: "Yes" },
        { id: "false", label: "No" },
      ],
      render: (person: Person) => (person.is_employee ? "Yes" : "No"),
      exportValue: (person: Person) => (person.is_employee ? "Yes" : "No"),
    },
    {
      key: "opportunity_count",
      title: "# Opportunities",
      sortable: true,
      filterable: true,
      filterType: "number",
      defaultSortDirection: "desc",
      nullSort: "min",
      size: 140, // Fixed
      render: (person: Person) => person.opportunity_count?.toString() || "0",
    },
    // Touchpoint metric columns - use metric_ prefix for consistent filter handling
    {
      key: "metric_touchpoint_count",
      title: "# Touchpoints",
      tooltip: "Count of touchpoints. Respects touchpoint filters.",
      columnType: "metric",
      category: "Touchpoint",
      categoryStyle: CATEGORY_STYLES.Touchpoint?.color,
      sortable: true,
      filterable: true,
      filterType: "number",
      defaultSortDirection: "desc",
      nullSort: "min",
      size: 140, // Fixed
      render: (person: Person) => person.touchpoint_count?.toString() || "0",
    },
    {
      key: "metric_first_touchpoint_date",
      title: "First Touchpoint Date",
      tooltip: "Date of the first touchpoint. Respects touchpoint filters.",
      columnType: "metric",
      category: "Touchpoint",
      categoryStyle: CATEGORY_STYLES.Touchpoint?.color,
      sortable: true,
      filterable: true,
      filterType: "date",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 180, // Fixed
      defaultHidden: true, // Hidden by default, users can enable
      render: (person: Person) =>
        person.first_touchpoint_date
          ? formatDate(person.first_touchpoint_date)
          : "-",
      exportValue: (person: Person) =>
        person.first_touchpoint_date
          ? formatDate(person.first_touchpoint_date)
          : "-",
    },
    {
      key: "metric_last_touchpoint_date",
      title: "Last Touchpoint Date",
      tooltip: "Date of the last touchpoint. Respects touchpoint filters.",
      columnType: "metric",
      category: "Touchpoint",
      categoryStyle: CATEGORY_STYLES.Touchpoint?.color,
      sortable: true,
      filterable: true,
      filterType: "date",
      defaultSortDirection: "desc",
      nullSort: "last",
      size: 180, // Fixed
      defaultHidden: true, // Hidden by default, users can enable
      render: (person: Person) =>
        person.last_touchpoint_date
          ? formatDate(person.last_touchpoint_date)
          : "-",
      exportValue: (person: Person) =>
        person.last_touchpoint_date
          ? formatDate(person.last_touchpoint_date)
          : "-",
    },
  ];

  // Append custom field columns if provided
  if (customFields && customFields.length > 0) {
    const customFieldColumns = createCustomFieldColumns(customFields);
    return [
      ...standardColumns,
      ...customFieldColumns,
    ] as UnifiedTableColumn<Person>[];
  }

  return standardColumns;
};

// Convert UnifiedTableColumn to TanStack Table ColumnDef
export const getPersonColumnDefs = (
  _filterOptions?: Record<string, never>,
  customFields?: CustomFieldDefinition[],
): ColumnDef<Person>[] => {
  const columns = getPersonColumns(_filterOptions, customFields);

  return columns
    .filter((col) => !col.exportOnly)
    .map((col) => {
      // For each column, create the column definition
      return {
        id: col.key.toString(),
        accessorKey: col.key,
        header: col.title,
        cell: ({ row }) => {
          const personData = row.original;
          if (!personData) {
            return "-"; // Handle cases where row data might be undefined
          }
          return col.render
            ? col.render(personData)
            : String(personData[col.key as keyof Person] || "");
        },
        meta: {
          title: col.title,
          tooltip: col.tooltip, // For metric column tooltips
          categoryStyle: col.categoryStyle, // For metric column styling
          sortable: col.sortable,
          filterable: col.filterable,
          filterType: col.filterType,
          filterOptions: col.filterOptions,
          defaultSortDirection: col.defaultSortDirection,
          nullSort: col.nullSort,
          isCustomField: col.isCustomField,
          customFieldDataType: col.customFieldDataType,
          dataType: col.dataType, // Include simple data type string
          required: col.required, // Pass through the required flag
        },
        size: col.size || DEFAULT_COLUMN_WIDTH, // Use column-specific size or default
      };
    });
};
